import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Modal from '@homeaway/react-modal';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {
    DayAvailability,
    CheckInCheckOut,
    DatePickerIcon,
} from '@homeaway/react-search-components';
import {DateRangePicker, dateUtils} from '@homeaway/react-date-pickers';
import GuestPicker from './GuestPicker';
import classNames from 'classnames';
import edapWrapper from '@homeaway/edap-wrapper';
import {
    removeDatesFromSearchCookie,
    removeGuestDataFromSearchCookie,
    getDatesFromCookie,
} from '../../utils/cookies';
import {getSerpUrl} from '../../utils/search';
import {
    GENERIC_EVENT,
    PAGE_MODULE_VIEW,
    CONTENT_MODULE_PRESENTED,
    SEARCH_QUERY_SUBMITTED,
    SEARCH_LINK_CLICK,
    GUESTPICKER_PRESENTED,
    GUEST_SELECTOR_UPDATE,
    GUEST_UPDATE,
    getBaseEventData,
    getActionLocation,
    sendEdapEventFastTrack,
} from './events';

import './style.less';

class DateSearchModal extends Component {
    static propTypes = {
        /** Modal Component id */
        modalId: PropTypes.string.isRequired,
        /** Show/Hide modal  */
        showDatesModal: PropTypes.bool.isRequired,
        /** Image to be shown in the modal */
        thumbnailUrl: PropTypes.string,
        /** Alt Image text */
        altImgText: PropTypes.string,
        /** Whether it is mobile */
        isMobile: PropTypes.bool,
        /** Messages, all component texts/translations */
        messages: PropTypes.shape({
            datePicker: PropTypes.shape({
                labels: PropTypes.string.isRequired,
                done: PropTypes.string.isRequired,
                dates: PropTypes.string.isRequired,
                checkOut: PropTypes.string.isRequired,
                checkIn: PropTypes.string.isRequired,
            }),
            modal: PropTypes.shape({
                subheading: PropTypes.string.isRequired,
                subheadingv2: PropTypes.string, // TO BE ADDED TO BABEL PRIME
                title: PropTypes.string.isRequired,
                button: PropTypes.shape({
                    search: PropTypes.string.isRequired,
                    skip: PropTypes.string, // TO BE ADDED TO BABEL PRIME
                }),
            }),
            guests: PropTypes.shape({
                label: PropTypes.string.isRequired,
                adult: PropTypes.string.isRequired,
                adults: PropTypes.string.isRequired,
                child: PropTypes.string.isRequired,
                children: PropTypes.string.isRequired,
                pets: PropTypes.string.isRequired,
                guest: PropTypes.string.isRequired,
                guests: PropTypes.string.isRequired,
                yesBtn: PropTypes.string.isRequired,
                noBtn: PropTypes.string.isRequired,
                applyBtn: PropTypes.string.isRequired,
                modalTitle: PropTypes.string.isRequired,
                mobileBtn: PropTypes.string.isRequired,
                petMessage: PropTypes.string.isRequired,
            }),
        }),
        /** Arrival date */
        arrivalDate: PropTypes.string,
        /** Departure date */
        departureDate: PropTypes.string,
        /** Adults count */
        adultsCount: PropTypes.number,
        /** Children count */
        childrenCount: PropTypes.number,
        /** pets included */
        petIncluded: PropTypes.bool,
        /** Function to be called when selected dates change */
        onDateRangeChange: PropTypes.func,
        /** Function to be called when dates are cleared */
        onDateModalClear: PropTypes.func,
        /** Function to be called when selected guests change */
        onGuestPickerChange: PropTypes.func,
        /** Function to be called when guests are cleared */
        onGuestSelectorCleared: PropTypes.func,
        /** Function to be called when dates modal is closed */
        onDateModalClose: PropTypes.func,
        /** Function to be called when modal is closed */
        onModalClose: PropTypes.func,
        // /** Function to call when search is submitted */
        onSubmit: PropTypes.func,
        /** Edap action localion */
        edapActionLocation: PropTypes.string,
        /** Edap event refinements */
        edapEventRefinements: PropTypes.string,
        /** Datepicker Date Format Options */
        dateFormatOptions: PropTypes.shape({
            month: PropTypes.string,
            day: PropTypes.string,
            weekday: PropTypes.string,
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** channel to be used in the serp url construction. Example value: 'slp' */
        channel: PropTypes.string,
        /** listingId to be used in the serp url construction */
        tripleId: PropTypes.string,
        /** refinementKey fabId filter, used in the serp URL construction. Example: 27 */
        refinementKey: PropTypes.string,
        /** keywords to be used in the serp URL construction */
        keywords: PropTypes.string,
        /** brand name. Example: 'vrbo' */
        siteName: PropTypes.string,
        /** shows the guest picket and the skip button */
        showGuestPicker: PropTypes.bool,
    };

    static defaultProps = {
        modalId: 'dated-search-modal',
        openSearchInNewWindow: true,
        altImgText: '',
        dateFormatOptions: {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
        },
        showGuestPicker: false,
        onDateRangeChange: /* istanbul ignore next */ () => {},
        onDateModalClear: /* istanbul ignore next */ () => {},
        onGuestPickerChange: /* istanbul ignore next */ () => {},
        onGuestSelectorCleared: /* istanbul ignore next */ () => {},
        onDateModalClose: /* istanbul ignore next */ () => {},
        onModalClose: /* istanbul ignore next */ () => {},
        onSubmit: /* istanbul ignore next */ () => {},
    };

    state = {
        isLoading: false,
        isLoadingSkip: false,
    };

    componentDidMount() {
        const searchCookies = getDatesFromCookie();

        const {
            arrivalDate,
            departureDate,
            adultsCount,
            childrenCount,
            petIncluded,
        } = this.props;

        this.setState({
            // eslint-disable-line react/no-did-mount-set-state
            checkIn: arrivalDate ? arrivalDate : searchCookies.arrivalDate,
            checkOut: departureDate
                ? departureDate
                : searchCookies.departureDate,
            adultsCount: adultsCount ? adultsCount : searchCookies.adultsCount,
            childrenCount: childrenCount
                ? childrenCount
                : searchCookies.childrenCount,
            petIncluded: petIncluded ? petIncluded : searchCookies.petIncluded,
        });
    }

    getPrettyCheckInOut(checkIn, checkOut, inModal = false) {
        const {
            showDatesModal,
            messages: {datePicker: label},
        } = this.props;

        if (this.state.datePickerFocused !== true && !checkIn && !checkOut) {
            return <span />;
        }

        return (
            <CheckInCheckOut
                isOpen={showDatesModal}
                inModal={inModal}
                checkIn={checkIn}
                checkInLabel={label.checkIn}
                checkOut={checkOut}
                checkOutLabel={label.checkOut}
                ref={'check-in-out'}
            />
        );
    }

    onModalClose = () => {
        this.setState({
            isLoading: false,
            isLoadingSkip: false,
        });
    };

    onClose = () => {
        const {edapActionLocation} = this.props;
        this.setState({
            tripleId: null,
            altImgText: null,
        });

        edapWrapper().push({
            name: GENERIC_EVENT,
            data: {
                actionlocation: getActionLocation(edapActionLocation),
                eventcategory: 'dated-search-modal',
                eventaction: 'modal-dismissed',
            },
        });

        this.props.onModalClose();
    };

    onModalOpen = () => {
        const {edapActionLocation} = this.props;

        this.sendModuleViewEdap();

        sendEdapEventFastTrack(edapActionLocation, CONTENT_MODULE_PRESENTED);
    };

    onDateRangeFocus = () => {
        this.setState({datePickerFocused: true});
    };

    onDateRangeChange = (checkIn, checkOut) => {
        this.setState({
            checkIn,
            checkOut,
            showContinueButton: !!(
                (checkIn && checkOut) ||
                (!checkIn && !checkOut)
            ),
        });

        this.props.onDateRangeChange(checkIn, checkOut);
    };

    onDateModalClear = () => {
        this.setState({
            checkIn: null,
            checkOut: null,
            showContinueButton: false,
        });

        removeDatesFromSearchCookie();

        this.props.onDateModalClear();
    };

    onDateModalClose = () => {
        const state = {
            datePickerFocused: false,
            tripleId: null,
        };

        if (!this.state.checkIn || !this.state.checkOut) {
            Object.assign(state, {
                checkIn: null,
                checkOut: null,
                showContinueButton: false,
            });
        }

        this.setState(state);

        this.props.onDateModalClose();
    };

    onSubmit = (isSkip = false) => {
        if (isSkip) {
            this.setState({
                isLoadingSkip: true,
            });
        } else {
            this.setState({
                isLoading: true,
            });
        }

        const {
            arrivalDate,
            departureDate,
            refinementKey,
            siteName,
            channel,
            tripleId,
            keywords,
            showGuestPicker,
            edapActionLocation,
        } = this.props;

        const {adultsCount, childrenCount, petIncluded} = this.state;

        const checkIn = this.state.checkIn || arrivalDate;
        const checkOut = this.state.checkOut || departureDate;
        const hasDates = !!(checkIn && checkOut);

        if (!hasDates) {
            removeDatesFromSearchCookie();
        }

        if (!isSkip) {
            this.sendSubmitDataToEdap();
        }

        const serpUrl = getSerpUrl({
            datedSearchSubmit: hasDates,
            refinementKey,
            siteName,
            arrivalDate: checkIn,
            departureDate: checkOut,
            channel,
            tripleId,
            keywords,
            adultsCount,
            childrenCount,
            petIncluded,
            showGuestPicker,
        });

        sendEdapEventFastTrack(edapActionLocation, SEARCH_LINK_CLICK);
        if (!isSkip) {
            sendEdapEventFastTrack(edapActionLocation, SEARCH_QUERY_SUBMITTED);
        }
        this.props.onSubmit(serpUrl, hasDates, keywords);
    };

    sendSubmitDataToEdap() {
        const {
            edapActionLocation,
            arrivalDate,
            departureDate,
            edapEventRefinements,
        } = this.props;
        const checkIn = this.state.checkIn || arrivalDate;
        const checkOut = this.state.checkOut || departureDate;

        const eventType = ['search', 'submit'];
        const eventData = getBaseEventData(
            edapActionLocation,
            edapEventRefinements
        );

        if (checkIn && checkOut) {
            eventData.startdate = checkIn;
            eventData.enddate = checkOut;

            eventType.unshift('dated');
        }

        edapWrapper().push({
            name: eventType.join('.'),
            data: eventData,
        });
    }

    sendModuleViewEdap() {
        const {edapActionLocation, edapEventRefinements} = this.props;

        const eventData = getBaseEventData(
            edapActionLocation,
            edapEventRefinements
        );

        edapWrapper().push({
            name: PAGE_MODULE_VIEW,
            data: eventData,
        });
    }

    onGuestPickerChange = (key, value) => {
        this.setState({[key]: value});

        const {edapActionLocation} = this.props;
        const eventData = {};

        if (key === 'adultsCount') {
            eventData.adult_count = value;
        } else if (key === 'childrenCount') {
            eventData.child_count = value;
        } else if (key === 'petIncluded') {
            eventData.with_pets = value;
        }

        sendEdapEventFastTrack(
            edapActionLocation,
            GUEST_SELECTOR_UPDATE,
            eventData
        );

        this.props.onGuestPickerChange(key, value);
    };

    onGuestSelectorClose = () => {
        const {edapActionLocation} = this.props;
        const {adultsCount, childrenCount, petIncluded} = this.state;

        sendEdapEventFastTrack(edapActionLocation, GUEST_UPDATE, {
            adult_count: adultsCount,
            child_count: childrenCount,
            with_pets: petIncluded,
        });
    };

    onGuestSelectorCleared = () => {
        this.setState({
            adultsCount: 0,
            childrenCount: 0,
            petIncluded: false,
        });

        removeGuestDataFromSearchCookie();

        this.props.onGuestSelectorCleared();
    };

    onGuestSelectorExited = (undoState) => {
        this.setState(undoState);
        return;
    };

    onGuestSelectorOpen = () => {
        const {edapActionLocation} = this.props;

        const {adultsCount, childrenCount, petIncluded} = this.state;

        sendEdapEventFastTrack(edapActionLocation, GUESTPICKER_PRESENTED, {
            adult_count: adultsCount,
            child_count: childrenCount,
            with_pets: petIncluded,
        });
    };

    render() {
        const {
            modalId,
            showDatesModal,
            thumbnailUrl,
            altImgText,
            isMobile,
            messages,
            arrivalDate,
            departureDate,
            dateFormatOptions,
            locale,
            showGuestPicker,
        } = this.props;

        const {adultsCount, childrenCount, petIncluded} = this.state;

        const checkIn = this.state.checkIn || arrivalDate;
        const checkOut = this.state.checkOut || departureDate;
        const showContinueButton =
            this.state.showContinueButton || !!(checkIn && checkOut);

        const datePickerLabels = messages.datePicker;
        const modalLabels = messages.modal;

        const checkInDate = dateUtils.getLocaleDateString(
            checkIn,
            locale,
            dateFormatOptions
        );
        const checkOutDate = dateUtils.getLocaleDateString(
            checkOut,
            locale,
            dateFormatOptions
        );

        const img = thumbnailUrl ? (
            <div className="DSM__image">
                <img src={thumbnailUrl} alt={altImgText} />
            </div>
        ) : null;

        const skipBtn = (
            <button
                type={'submit'}
                className={'btn DSM__btn DSM__btn_skip'}
                onClick={() => this.onSubmit(true)}
            >
                {modalLabels.button.skip}
                <LoadingOverlay loading={this.state.isLoadingSkip} />
            </button>
        );

        const btnGroup = (
            <div className={'DSM__btn_group'}>
                {showGuestPicker && !isMobile && skipBtn}
                <button
                    type={'submit'}
                    className={'btn btn-primary DSM__btn'}
                    onClick={() => this.onSubmit()}
                >
                    {modalLabels.button.search}
                    <LoadingOverlay loading={this.state.isLoading} />
                </button>
                {showGuestPicker && isMobile && skipBtn}
            </div>
        );

        const dateSelectionCls = checkIn // eslint-disable-line no-nested-ternary
            ? checkOut
                ? 'dates-selected-both'
                : 'dates-selected-checkin-only'
            : 'dates-selected-none';

        const classesCont = thumbnailUrl
            ? classNames('DSM__container')
            : classNames('DSM__container', 'DSM__container--noImage');

        const classesBody = showGuestPicker
            ? classNames('DSM__body', 'DSM__body_guests')
            : classNames('DSM__body');

        return (
            <Modal
                id={modalId}
                title={modalLabels.title}
                isOpen={showDatesModal}
                onClose={this.onClose}
                onModalClose={this.onModalClose}
                onModalOpen={this.onModalOpen}
                className={'DSM'}
                footer={isMobile ? btnGroup : null}
                header
                closeOnOutsideClick
            >
                {isMobile && img}
                <p>
                    {showGuestPicker
                        ? modalLabels.subheadingv2
                        : modalLabels.subheading}
                </p>
                <div className={classesBody}>
                    {!isMobile && img}
                    <div className={`${classesCont} ${dateSelectionCls}`}>
                        <div className="DSM__dates">
                            <DateRangePicker
                                className={'DSM__range'}
                                isMobile={isMobile}
                                modalTitle={this.getPrettyCheckInOut(
                                    checkInDate,
                                    checkOutDate,
                                    true
                                )}
                                showContinueButton={showContinueButton}
                                dateFormatOptions={dateFormatOptions}
                                startDate={checkIn}
                                endDate={checkOut}
                                consolidateInputs
                                dayTemplateComponent={DayAvailability}
                                onDateRangeChange={this.onDateRangeChange}
                                onDateRangeClear={this.onDateModalClear}
                                onClose={this.onDateModalClose}
                                onOpen={this.onDateRangeFocus}
                                continueButtonLabel={datePickerLabels.done}
                                clearButtonLabel={datePickerLabels.labels}
                                inputLabel1={datePickerLabels.dates}
                                inputLabel2={datePickerLabels.checkOut}
                                inputIcon1={<DatePickerIcon />}
                                inputName1={'date-modal-checkin'}
                                inputName2={'date-modal-checkout'}
                                id={`${modalId}-datepicker`}
                            />
                            {this.getPrettyCheckInOut(
                                checkInDate,
                                checkOutDate
                            )}
                        </div>
                        {showGuestPicker && (
                            <GuestPicker
                                onChange={this.onGuestPickerChange}
                                onClear={this.onGuestSelectorCleared}
                                onExit={this.onGuestSelectorExited}
                                onClose={this.onGuestSelectorClose}
                                onOpen={this.onGuestSelectorOpen}
                                messages={messages.guests}
                                {...{
                                    adultsCount,
                                    childrenCount,
                                    petIncluded,
                                }}
                            />
                        )}
                        {!isMobile && btnGroup}
                    </div>
                </div>
            </Modal>
        );
    }
}

export default DateSearchModal;
